import * as React from "react";
import * as R from "ramda";
import GroupSelectorItems, { hasItemsSelected } from "./GroupSelectorItems";
import { Base } from "./styles";

export type ItemProps = {
  title: string;
  id: string;
  selected?: boolean;
  open?: boolean;
  items?: ItemProps[];
};

/**
 * Notes Erik (code-nazi-modus ;):
 * - Ik zou proberen de types in volgorde te zetten van wanneer ze gebruikt worden
 *   Anders moet je naar onderen scrollen om te bekijken wat iets betekend
 * - Vanwege complexiteit van deze file zou ik bijv styles even in losse file zetten.
 *   Misschien Item component ook.
 */

/**
 * Ik zou structure de volgende type geven:
 * type Structure = {
 *  id: string;
 *  items?: Structure[]
 * }
 * Op die manier weet de functie alleen het noodzakelijke, en is het niet sterk gekoppeld
 * aan dit specifieke component, bijv voor als we het later ergens opnieuw willen gebruiken...
 */

type Structure = {
  id: string;
  items?: Structure[];
};

export function findPath(id: string, structure: Structure[]): any[] | null {
  for (let i = 0; i < structure.length; i++) {
    const currentItem = structure[i];
    if (currentItem.id === id) {
      return [i];
    } else if (currentItem.items) {
      const result = findPath(id, currentItem.items);
      if (result) {
        return [i, "items", ...result];
      }
    }
  }
  return null;
}

function hasAllItemsSelected(items: any[]) {
  return items.every(item => item.selected);
}

/**
 * Erik: ik heb het idee dat deze functie te veel doet. Althans, meer dan
 * de naam doet vermoeden. Het gedrag dat children ook geupdate worden (en waarom)
 * Kan denk ik beter in andere functie.
 *
 */

function updateStructure(
  items: Structure[],
  item: any,
  path: any[],
  partial: any
) {
  return R.assocPath(path, { ...item, ...partial }, items) as any;
}

function loopOverKey(arr: any[], key: string, callback: (item: any) => any) {
  return arr.map(x => {
    if (x[key]) {
      x[key] = loopOverKey(x[key], key, callback);
    }
    return callback(x);
  });
}

function updateChildren(
  items: ItemProps[],
  partial?: any,
  callback?: (item: ItemProps) => ItemProps
) {
  return loopOverKey(items, "items", item => {
    if (callback) {
      return callback(item);
    }
    return {
      ...item,
      ...partial
    };
  });
}

function setSelectedItemsOpen(items: ItemProps[]): ItemProps[] {
  return loopOverKey(items, "items", item => {
    return {
      ...item,
      items: item.items && setSelectedItemsOpen(item.items),
      open:
        item.items &&
        item.items.some(
          (x: ItemProps) =>
            item.selected || x.selected || hasItemsSelected(x.items)
        )
    };
  });
}

function getParent(path: any[], items: any[]) {
  const parent = R.path(path, items) as any;
  return !parent.length ? parent : null;
}

function updateItem(
  items: Structure[],
  id: Structure["id"],
  partial: any
): ItemProps[] {
  const path = findPath(id, items) as any[];
  const item = R.path(path, items) as any;

  const newItem = {
    ...item,
    items: item.items
      ? updateChildren(item.items, { selected: false })
      : item.items
  };

  const newStructure = updateStructure(items, newItem, path, partial);
  const parentPath = R.remove(path.length - 2, 2, path);
  const parent = getParent(parentPath, newStructure);

  if (parent) {
    if (hasAllItemsSelected(parent.items)) {
      return updateItem(newStructure, parent.id, { selected: true });
    }

    if (parent.selected) {
      return updateItem(newStructure, parent.id, {
        selected: false,
        items: updateChildren(parent.items, null, item => {
          return {
            ...item,
            selected: item.id === newItem.id ? false : true
          };
        })
      });
    }
  }

  return newStructure;
}

type Props = {
  style?: React.CSSProperties;
  className?: string;
  items: ItemProps[];
  onChange: (items: ItemProps[]) => void;
  setSelectedOpen?: boolean;
  showOnlySelected?: boolean;
  stretch?: boolean;
};

// - value misschien renamen naar items?
// - die extra div eromheen voelt beetje overbodig?
function GroupSelector(
  {
    style,
    className,
    items,
    onChange,
    showOnlySelected,
    setSelectedOpen,
    stretch
  }: Props,
  ref: any
) {
  const changeHandler = (
    id: ItemProps["id"],
    selected: ItemProps["selected"]
  ) => {
    const result = updateItem(items, id, { selected: !selected });
    onChange(result);
  };

  return (
    <Base ref={ref} style={style} className={className} stretch={stretch}>
      {items && (
        <GroupSelectorItems
          items={setSelectedOpen ? setSelectedItemsOpen(items) : items}
          showOnlySelected={showOnlySelected}
          onChange={changeHandler}
        />
      )}
    </Base>
  );
}

export default React.forwardRef(GroupSelector);
